import type { JSX, TargetedMouseEvent } from 'preact';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { setNextPage, setPageNumber, setPrevPage } from 'Core/actions/request';
import { useDispatch } from 'Core/store';
import { cloneSafe } from 'Utils/components';

import type { PaginationButtonType } from './propsHelper';

type PaginationAction =
  | ReturnType<typeof setNextPage>
  | ReturnType<typeof setPrevPage>
  | ReturnType<typeof setPageNumber>;

const typeToActionMap: Record<string, ((n: number) => PaginationAction) | null> = {
  next: setNextPage,
  prev: setPrevPage,
  page: setPageNumber,
  delimiter: null,
};

interface Props {
  template: TemplateFunction<PaginationButtonTemplateParams>;
  type: PaginationButtonType;
  pageLink?: string;
  pageNumber?: number;
  totalPages?: number;
  isActive?: boolean;
  style?: JSX.CSSProperties;
  hideDropdown?: () => void;
}

export interface PaginationButtonTemplateParams {
  isActive?: boolean;
  page?: number;
  pageLink?: string;
  template: string;
  totalPages?: number;
}

export default function PaginationButton({
  template,
  type,
  pageLink,
  pageNumber,
  totalPages,
  isActive,
  style,
  hideDropdown,
}: Props): TemplateResult {
  const dispatch = useDispatch();
  const actionCreator = typeToActionMap[type];

  let props: { style?: JSX.CSSProperties; onClick?: (e: TargetedMouseEvent<HTMLElement>) => void } = {
    style,
  };
  let component;
  if (actionCreator) {
    const onClick = (e: TargetedMouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (!isActive) {
        if (hideDropdown) {
          hideDropdown();
        }
        dispatch(actionCreator(pageNumber ?? 0));
      }
    };
    component = template.call({
      template: type,
      page: (pageNumber ?? 0) + 1,
      totalPages,
      isActive,
      pageLink,
    });
    props = { ...props, onClick };
  } else {
    component = template.call({ template: type });
  }

  return cloneSafe(component, null, { ...props });
}
